// game.jsx — the pirate cabin (reference UI #4-#12; old/room.php puzzle chain)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../sessionSlice";
import "./game.css";
import { fireGameAlert } from "./gameAlert";
import {
  Parchment,
  PirateButton,
  useDisableInspect,
  useSceneBackground,
} from "../escapeShared";
import deskBg from "../img/room/desktop-bg.jpg";
import deskCupboard from "../img/room/desktop-cupboard.png";
import deskSpeaker from "../img/room/desktop-speaker.png";
import deskMap from "../img/room/desktop-map.png";
import deskTreasure from "../img/room/desktop-treasure.png";
import mobBg from "../img/room/mobile-bg.jpg";
import mobCupboard from "../img/room/mobile-cupboard.png";
import mobSpeaker from "../img/room/mobile-speaker.png";
import mobMap from "../img/room/mobile-map.png";
import mobTreasure from "../img/room/mobile-treasure.png";
import jigsawBg from "../img/bg4.jpg";
import wordBg from "../img/bg5.jpg";
import finalBg from "../img/bg6.jpg";
import mobWoodBg from "../img/mob_bg2.jpg";
import mobFinalBg from "../img/mob_bg4.jpg";
import bulbImg from "./img/escape/bulb.png";
import jewelIconImg from "./img/escape/jewel-icon.png";
import jisImg from "./img/escape/jis.png";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  getStageSessionUserId,
  hydrateCompletedStagesFromRecord,
  markGameOver,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";

const STAGE_NUM = 1;
const audioClip =
  "https://2023-extramileplay-php.s3.ap-south-1.amazonaws.com/UAT/escape_room/new.mp3";
const ROOM_DURATION_SEC = 20 * 60; // old/room.php's fixed 20-minute countdown

const CONNECTION_ERROR = "Could not reach the server. Check your connection and try again.";

// jis.png is 500x292; the jigsaw grid keeps that ratio so tiles aren't stretched.
const JIS_IMAGE_ASPECT_RATIO = "500 / 292";

/** Layers of img/room.svg and img/room_mob.svg (same ids, same transforms),
 *  plus the books and pirate flag painted into each background. */
const ROOM_SCENES = {
  desktop: {
    width: 1920,
    height: 1080,
    bg: deskBg,
    layers: {
      cupboard: { href: deskCupboard, x: 32.16, y: 588.79, w: 460, h: 420 },
      speaker: { href: deskSpeaker, x: 1647, y: 740.15, w: 106, h: 191 },
      map: { href: deskMap, x: 1407.57, y: 178.43, w: 335, h: 346 },
      treasure: { href: deskTreasure, x: 1361.57, y: 607.98, w: 427, h: 284 },
    },
    zones: {
      books: { x: 668, y: 878, w: 172, h: 98 },
      flag: { x: 1135, y: 155, w: 172, h: 208 },
    },
  },
  mobile: {
    width: 414,
    height: 896,
    bg: mobBg,
    layers: {
      speaker: { href: mobSpeaker, x: 363.95, y: 639.75, w: 51 * 0.98, h: 113 * 0.98 },
      treasure: { href: mobTreasure, x: 259.04, y: 558.99, w: 157 * 0.99, h: 138 * 0.99 },
      cupboard: { href: mobCupboard, x: 0, y: 505.77, w: 119 * 0.99, h: 170 * 0.99 },
      map: { href: mobMap, x: 272.64, y: 239.75, w: 124 * 1.03, h: 165 * 1.03 },
    },
    zones: {
      books: { x: 18, y: 660, w: 86, h: 56 },
      flag: { x: 242, y: 146, w: 66, h: 102 },
    },
  },
};

// old/room.php's #myModal "Cryptic gibberish" lines.
const GIBBERISH_LINES = [
  "Dizz ees r th irdd hi esst",
  "V knea dd phour sskayp phlaanz",
  "Kno, v own ley knea dd waun ghr ate phlaan",
  "Arr sthraa tigy izz flow lisp widd jee roh aerors",
  "V saill go oldh juuls en meyk phhive mil leon dollezz",
];

// old/js/wordpuzzle.js, each found word painted its own colour (reference #8).
const WORD_COLORS = {
  BLUE: "#2f8fe8",
  BLACK: "#3a3a3a",
  RED: "#e5384f",
  GREEN: "#3ccf4e",
  YELLOW: "#f5d90a",
};
const WORDS = Object.keys(WORD_COLORS);
const WORD_GRID_SIZE = 10;

const DEMO_EXIT_TEXT = "Thank you for playing. Subscribe to any PLAN to play with your peers.";

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

/** Shuffle guaranteeing a full derangement — no tile starts in its correct
 *  spot (old puzzle.js on "expert" never shows any square pre-solved either). */
function shuffleOrder(size) {
  const arr = Array.from({ length: size }, (_, i) => i);
  if (size <= 1) return arr;
  const hasFixedPoint = (a) => a.some((v, i) => v === i);
  do {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  } while (hasFixedPoint(arr));
  return arr;
}

/** old/room.php's #puzzle1 jigsaw: drag (mouse + touch via Pointer Events) or
 *  tap two tiles to swap. Misplaced tiles are grayscale and turn orange once in
 *  place (old css/puzzle.css), and a placed tile locks there. */
function JigsawPuzzle({ image, size = 3, aspectRatio = "1 / 1", startSolved = false, onSolved }) {
  const total = size * size;
  const [order, setOrder] = useState(() =>
    startSolved ? Array.from({ length: total }, (_, i) => i) : shuffleOrder(total)
  );
  const [selected, setSelected] = useState(null);
  const [dragOverPos, setDragOverPos] = useState(null);
  const [solved, setSolved] = useState(startSolved);
  const [draggingPos, setDraggingPos] = useState(null);
  const [ghost, setGhost] = useState(null);
  const dragStateRef = useRef(null); // { pos, startX, startY, width, height, moved }
  const suppressClickRef = useRef(false); // swallow the click a real drag leaves behind

  useEffect(() => {
    if (solved) return;
    if (order.every((v, i) => v === i)) {
      setSolved(true);
      onSolved?.();
    }
  }, [order, solved, onSolved]);

  const swapTiles = useCallback((a, b) => {
    if (a == null || b == null || a === b) return;
    setOrder((prev) => {
      if (prev[a] === a || prev[b] === b) return prev;
      const next = [...prev];
      [next[a], next[b]] = [next[b], next[a]];
      return next;
    });
  }, []);

  const handleTileClick = useCallback(
    (pos) => {
      if (suppressClickRef.current) {
        suppressClickRef.current = false;
        return;
      }
      if (solved || order[pos] === pos) return;
      setSelected((sel) => {
        if (sel === null) return pos;
        if (sel === pos) return null;
        swapTiles(sel, pos);
        return null;
      });
    },
    [solved, swapTiles, order]
  );

  const handlePointerDown = useCallback(
    (pos) => (e) => {
      if (solved || order[pos] === pos) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      dragStateRef.current = {
        pos,
        startX: e.clientX,
        startY: e.clientY,
        width: rect.width,
        height: rect.height,
        moved: false,
      };
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        // Some touch browsers refuse capture; move/up handlers still work.
      }
    },
    [solved, order]
  );

  const findTilePosAt = useCallback((x, y) => {
    const tileEl = document.elementFromPoint(x, y)?.closest?.("[data-jigsaw-pos]");
    if (!tileEl) return null;
    const pos = Number(tileEl.dataset.jigsawPos);
    return Number.isFinite(pos) ? pos : null;
  }, []);

  const handlePointerMove = useCallback(
    (e) => {
      const drag = dragStateRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      if (!drag.moved && Math.hypot(dx, dy) > 6) {
        drag.moved = true;
        setDraggingPos(drag.pos);
        setSelected(null);
      }
      if (!drag.moved) return;
      setGhost({ x: e.clientX, y: e.clientY, width: drag.width, height: drag.height });
      const overPos = findTilePosAt(e.clientX, e.clientY);
      setDragOverPos(
        overPos != null && overPos !== drag.pos && order[overPos] !== overPos ? overPos : null
      );
    },
    [order, findTilePosAt]
  );

  const endDrag = useCallback(
    (e) => {
      const drag = dragStateRef.current;
      dragStateRef.current = null;
      if (!drag) return;
      if (drag.moved) {
        suppressClickRef.current = true;
        const overPos = findTilePosAt(e.clientX, e.clientY);
        if (overPos != null) swapTiles(drag.pos, overPos);
      }
      setDraggingPos(null);
      setDragOverPos(null);
      setGhost(null);
    },
    [findTilePosAt, swapTiles]
  );

  const tileBackground = (tileIndex) => ({
    backgroundImage: `url(${image})`,
    backgroundSize: `${size * 100}% ${size * 100}%`,
    backgroundPosition: `${((tileIndex % size) * 100) / (size - 1)}% ${
      (Math.floor(tileIndex / size) * 100) / (size - 1)
    }%`,
  });

  return (
    <>
      <div className="escape-jigsaw-progress" aria-live="polite">
        {solved
          ? "Puzzle solved!"
          : `${order.filter((v, i) => v === i).length} / ${total} pieces placed`}
      </div>
      <div
        className={"escape-jigsaw-grid" + (solved ? " is-solved" : "")}
        style={{
          gridTemplateColumns: `repeat(${size}, 1fr)`,
          gridTemplateRows: `repeat(${size}, 1fr)`,
          aspectRatio,
        }}
      >
        {order.map((tileIndex, pos) => {
          const locked = tileIndex === pos;
          const isDragging = draggingPos === pos;
          return (
            <div
              key={pos}
              role="button"
              tabIndex={solved || locked ? -1 : 0}
              data-jigsaw-pos={pos}
              className={
                "escape-jigsaw-tile" +
                (selected === pos ? " is-selected" : "") +
                (dragOverPos === pos ? " is-dragover" : "") +
                (locked ? " is-locked" : "") +
                (isDragging ? " is-dragging" : "")
              }
              onClick={() => handleTileClick(pos)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleTileClick(pos);
                }
              }}
              onPointerDown={handlePointerDown(pos)}
              onPointerMove={handlePointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              style={{
                touchAction: locked || solved ? "auto" : "none",
                ...(isDragging ? { backgroundImage: "none" } : tileBackground(tileIndex)),
              }}
              aria-label={`Puzzle tile ${pos + 1}${locked ? " (in place)" : ""}`}
            />
          );
        })}
      </div>

      {ghost &&
        draggingPos != null &&
        createPortal(
          <div
            className="escape-jigsaw-ghost"
            style={{
              left: ghost.x - ghost.width / 2,
              top: ghost.y - ghost.height / 2,
              width: ghost.width,
              height: ghost.height,
              ...tileBackground(order[draggingPos]),
            }}
          />,
          document.body
        )}
    </>
  );
}

/** old/js/wordpuzzle.js grid builder: words go right, down or down-right. */
function buildWordGrid() {
  const grid = Array.from({ length: WORD_GRID_SIZE }, () => Array(WORD_GRID_SIZE).fill(""));
  const steps = [
    [0, 1],
    [1, 0],
    [1, 1],
  ];
  WORDS.forEach((word) => {
    for (let attempt = 0; attempt < 100; attempt++) {
      const [dr, dc] = steps[Math.floor(Math.random() * steps.length)];
      const row = Math.floor(Math.random() * WORD_GRID_SIZE);
      const col = Math.floor(Math.random() * WORD_GRID_SIZE);
      const endRow = row + dr * (word.length - 1);
      const endCol = col + dc * (word.length - 1);
      if (endRow >= WORD_GRID_SIZE || endCol >= WORD_GRID_SIZE) continue;
      let free = true;
      for (let i = 0; i < word.length; i++) {
        if (grid[row + dr * i][col + dc * i] !== "") free = false;
      }
      if (!free) continue;
      for (let i = 0; i < word.length; i++) grid[row + dr * i][col + dc * i] = word[i];
      return;
    }
  });
  for (let r = 0; r < WORD_GRID_SIZE; r++) {
    for (let c = 0; c < WORD_GRID_SIZE; c++) {
      if (grid[r][c] === "") grid[r][c] = String.fromCharCode(65 + Math.floor(Math.random() * 26));
    }
  }
  return grid;
}

const cellKey = (r, c) => `${r}-${c}`;

/** old/js/wordpuzzle.js: drag from a word's first letter to its last (right,
 *  down or diagonally down-right) to select it. */
function WordSearch({ onComplete }) {
  const [grid] = useState(buildWordGrid);
  const [foundCells, setFoundCells] = useState(() => new Map()); // cellKey -> colour
  const [foundWords, setFoundWords] = useState(() => new Set());
  const [path, setPath] = useState([]);
  const startRef = useRef(null);
  const pathRef = useRef([]);
  const completedRef = useRef(false);

  const cellAt = (x, y) => {
    const td = document.elementFromPoint(x, y)?.closest?.("td[data-r]");
    return td ? { r: Number(td.dataset.r), c: Number(td.dataset.c) } : null;
  };

  const updatePath = (next) => {
    pathRef.current = next;
    setPath(next);
  };

  const handlePointerDown = (e) => {
    const cell = cellAt(e.clientX, e.clientY);
    if (!cell || foundCells.has(cellKey(cell.r, cell.c))) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    startRef.current = cell;
    updatePath([cell]);
  };

  const handlePointerMove = (e) => {
    const start = startRef.current;
    if (!start) return;
    const end = cellAt(e.clientX, e.clientY);
    if (!end) return;
    const dr = end.r - start.r;
    const dc = end.c - start.c;
    // old highlightPath(): straight or 45° lines, forward directions only.
    if (dr < 0 || dc < 0 || !(dr === 0 || dc === 0 || dr === dc)) return;
    const len = Math.max(dr, dc);
    const next = [];
    for (let i = 0; i <= len; i++) {
      const r = start.r + (dr ? i : 0);
      const c = start.c + (dc ? i : 0);
      if (!foundCells.has(cellKey(r, c))) next.push({ r, c });
    }
    updatePath(next);
  };

  const handlePointerUp = () => {
    if (!startRef.current) return;
    startRef.current = null;
    const cells = pathRef.current;
    updatePath([]);
    const word = cells.map(({ r, c }) => grid[r][c]).join("");
    const reversed = word.split("").reverse().join("");
    const match = WORDS.find((w) => w === word || w === reversed);
    if (!match || foundWords.has(match)) return;

    const nextWords = new Set(foundWords).add(match);
    setFoundWords(nextWords);
    setFoundCells((prev) => {
      const next = new Map(prev);
      cells.forEach(({ r, c }) => next.set(cellKey(r, c), WORD_COLORS[match]));
      return next;
    });
    if (nextWords.size >= WORDS.length && !completedRef.current) {
      completedRef.current = true;
      onComplete();
    }
  };

  const pathKeys = new Set(path.map(({ r, c }) => cellKey(r, c)));

  return (
    <table
      className="escape-word-grid"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <tbody>
        {grid.map((row, r) => (
          <tr key={r}>
            {row.map((letter, c) => {
              const key = cellKey(r, c);
              const color = foundCells.get(key);
              const inPath = pathKeys.has(key);
              return (
                <td
                  key={key}
                  data-r={r}
                  data-c={c}
                  className={color ? "is-found" : inPath ? "is-path" : ""}
                  style={color ? { background: color } : undefined}
                >
                  {letter}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Parchment dialog over the room (old/room.php's bootstrap modals). */
function RoomModal({ title, onClose, children, wide = false }) {
  return (
    <div className="escape-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <Parchment className={`escape-modal${wide ? " escape-modal--wide" : ""}`}>
        <button type="button" className="escape-modal__x" aria-label="Close" onClick={onClose}>
          &times;
        </button>
        {title ? <h3 className="escape-modal__title">{title}</h3> : null}
        {children}
      </Parchment>
    </div>
  );
}

/** old/room.php's code inputs. The server checks the answer, so the box only
 *  turns red after a wrong submit. */
function CodeModal({ prompt, onSubmit, onClose }) {
  const [value, setValue] = useState("");
  const [checking, setChecking] = useState(false);
  const [wrong, setWrong] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async () => {
    if (checking) return;
    setChecking(true);
    const correct = await onSubmit(value);
    setChecking(false);
    setWrong(!correct);
  };

  return (
    <RoomModal onClose={onClose}>
      <p className="escape-code-prompt">{prompt}</p>
      <input
        ref={inputRef}
        type="text"
        autoComplete="off"
        className={"escape-code-input" + (wrong ? " is-wrong" : "")}
        value={value}
        readOnly={checking}
        onChange={(e) => {
          setValue(e.target.value);
          setWrong(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.repeat) {
            e.preventDefault();
            submit();
          }
        }}
      />
      <div>
        <PirateButton onClick={submit} disabled={checking}>Submit</PirateButton>
      </div>
    </RoomModal>
  );
}

export default function Game() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  useDisableInspect();
  const isMobile = useSceneBackground(deskBg, mobBg);
  const scene = isMobile ? ROOM_SCENES.mobile : ROOM_SCENES.desktop;

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);

  // old/room.php's click-gated puzzle chain.
  const [activeModal, setActiveModal] = useState(null); // books | drawer | map | audio | last | null
  const [jigsawOpen, setJigsawOpen] = useState(false);
  const [wordOpen, setWordOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mapClickable, setMapClickable] = useState(false);
  const [mapSolved, setMapSolved] = useState(false); // old isAudioClickable
  const [flagClickable, setFlagClickable] = useState(false);
  const [wordFound, setWordFound] = useState(false);
  const [wordSolved, setWordSolved] = useState(false); // old islastButton
  const [chestOpen, setChestOpen] = useState(false);
  const [finished, setFinished] = useState(false);

  const finishedRef = useRef(false);
  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const submittedRef = useRef(false);
  const timeoutFiredRef = useRef(false);
  const introShownRef = useRef(false);
  const wordNextBusyRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  useEffect(() => {
    if (!dbReady) return;

    baseElapsedRef.current = elapsedRef.current;
    // performance.now() can't be moved by changing the device clock.
    const startedAt = performance.now();

    const tick = setInterval(() => {
      // Freeze the clock once the chest is open so the closing screens
      // aren't counted.
      if (finishedRef.current) return;
      const sessionSec = Math.floor((performance.now() - startedAt) / 1000);
      const elapsed = Math.min(ROOM_DURATION_SEC, baseElapsedRef.current + sessionSec);
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken || !uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);

        // old/room.php inserted the stat_escape row on first entry.
        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: uid, sessionId, organizationId, stageId: rowId }),
        });
        const stageData = await stageRes.json().catch(() => ({}));

        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          // old/room.php sent players with lifelines === "completed" to thankyou.
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          // The server's clock runs from stages.start_time, so a refresh keeps it going.
          const serverElapsed = Math.min(ROOM_DURATION_SEC, Number(st.elapsedSec) || 0);
          baseElapsedRef.current = serverElapsed;
          elapsedRef.current = serverElapsed;
          setElapsedSec(serverElapsed);

          // Put the player back on the puzzle they had reached.
          const progress = Number(st.currentQues) || 0;
          if (progress > 0 || serverElapsed >= ROOM_DURATION_SEC) introShownRef.current = true;
          if (progress >= 1) setDrawerOpen(true);
          if (progress === 1) setJigsawOpen(true);
          if (progress >= 2) setMapClickable(true);
          if (progress >= 3) setMapSolved(true);
          if (progress >= 4) {
            setFlagClickable(true);
            setWordFound(true);
            setWordSolved(true);
          }
          if (progress >= 5) {
            finishedRef.current = true;
            setFinished(true);
            setChestOpen(true);
          }
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  // old/room.php's opening hint.
  useEffect(() => {
    if (!dbReady || introShownRef.current) return;
    introShownRef.current = true;
    void fireGameAlert({
      text: "You see books in the room, could that be of any help?",
    });
  }, [dbReady]);

  /** old push(): the server works out score/time and flags the game over.
   *  Returns false when the save failed, so it can be tried again. */
  const saveFinalResult = useCallback(async () => {
    if (submittedRef.current) return true;

    const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);

    if (!isDemoBypass && backendBase && adminToken && stageId) {
      const saved = await saveGameStageAndReport({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId,
        stageNumber: STAGE_NUM,
      });
      if (!saved.ok) return false;
      const over = await markGameOver({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId,
      });
      if (!over.ok) return false;
    }

    submittedRef.current = true;
    clearStageActive(STAGE_NUM);
    markStageCompleted(STAGE_NUM, storedUser);
    return true;
  }, [storedUser, isDemoBypass, backendBase, adminToken, stageRowId]);

  const saveFinalResultWithRetry = useCallback(async () => {
    while (!(await saveFinalResult())) {
      await fireGameAlert({
        icon: "error",
        text: CONNECTION_ERROR,
        confirmButtonText: "Retry",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });
    }
  }, [saveFinalResult]);

  /** Server check of one puzzle step. null = unreachable; stop = already handled. */
  const submitStep = useCallback(
    async (step, answer) => {
      try {
        if (isDemoBypass) {
          const res = await fetch(`${backendBase}/demoCheckCode`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ answer }),
          });
          return res.ok ? await res.json() : null;
        }

        const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
        if (!backendBase || !adminToken || !stageId) return null;

        const res = await fetch(`${backendBase}/gameStepSubmit`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: getStageSessionUserId(storedUser),
            stageId,
            step,
            answer,
          }),
        });
        const data = await res.json().catch(() => ({}));

        if (data.gameOver) {
          navigate("/thankyou", { replace: true });
          return { stop: true };
        }
        if (data.timeUp) {
          baseElapsedRef.current = ROOM_DURATION_SEC;
          elapsedRef.current = ROOM_DURATION_SEC;
          setElapsedSec(ROOM_DURATION_SEC);
          return { stop: true };
        }
        // Out of step with the server, e.g. played on in another tab.
        if (res.status === 409) {
          window.location.reload();
          return { stop: true };
        }
        return res.ok && data.success ? data : null;
      } catch (err) {
        console.error("submitStep:", err);
        return null;
      }
    },
    [isDemoBypass, backendBase, adminToken, stageRowId, storedUser, navigate]
  );

  /** For the code popups: resolves true when the server accepted the answer. */
  const checkAnswer = useCallback(
    (step, onCorrect) => async (answer) => {
      const result = await submitStep(step, answer);
      if (!result) {
        void fireGameAlert({ icon: "error", text: CONNECTION_ERROR });
        return false;
      }
      if (result.stop) return false;
      if (result.correct) {
        onCorrect();
        return true;
      }
      void fireGameAlert({ icon: "error", text: "Wrong Code" });
      return false;
    },
    [submitStep]
  );

  /** For puzzles with no typed answer: keeps trying until the step is saved. */
  const completeStep = useCallback(
    async (step) => {
      let result = await submitStep(step);
      while (!result) {
        await fireGameAlert({
          icon: "error",
          text: CONNECTION_ERROR,
          confirmButtonText: "Retry",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });
        result = await submitStep(step);
      }
      return !result.stop;
    },
    [submitStep]
  );

  const freezeGame = useCallback(() => {
    finishedRef.current = true;
    setFinished(true);
    setActiveModal(null);
    setJigsawOpen(false);
    setWordOpen(false);
  }, []);

  // old/room.php's countdown expiry: "Times up!" -> thankyou.
  const remainingSec = Math.max(0, ROOM_DURATION_SEC - elapsedSec);
  useEffect(() => {
    if (!dbReady || finished || timeoutFiredRef.current || remainingSec > 0) return;
    timeoutFiredRef.current = true;
    elapsedRef.current = ROOM_DURATION_SEC;
    freezeGame();
    fireGameAlert({ icon: "error", title: "Times up!", allowOutsideClick: false, allowEscapeKey: false })
      .then(() => saveFinalResultWithRetry())
      .then(() => navigate("/thankyou"));
  }, [remainingSec, dbReady, finished, freezeGame, saveFinalResultWithRetry, navigate]);

  /** old closeAllPopups(): only one popup visible at a time. */
  const openOnly = useCallback((which) => {
    setActiveModal(["books", "drawer", "map", "audio", "last"].includes(which) ? which : null);
    setJigsawOpen(which === "jigsaw");
    setWordOpen(which === "word");
  }, []);

  const closeModal = useCallback(() => setActiveModal(null), []);

  // --- Step 1: books -> gibberish -> drawer code ---
  const handleDrawerSuccess = useCallback(() => {
    setActiveModal(null);
    setDrawerOpen(true);

    if (isDemoBypass) {
      freezeGame();
      void fireGameAlert({
        icon: "success",
        text: DEMO_EXIT_TEXT,
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => {
        window.location.href = String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
      });
      return;
    }

    window.setTimeout(() => {
      if (finishedRef.current) return;
      void fireGameAlert({
        title: "",
        text:
          "Now that you have unlocked the drawer, you find tons of hay and in between, you are able to grab shredded pieces of cardboard. Can you unpuzzle it?",
      }).then(() => {
        if (!finishedRef.current) openOnly("jigsaw");
      });
    }, 800);
  }, [isDemoBypass, openOnly, freezeGame]);

  // --- Step 2: jigsaw -> coordinates ---
  const handleJigsawSolved = useCallback(async () => {
    if (!(await completeStep("jigsaw"))) return;
    window.setTimeout(() => {
      if (finishedRef.current) return;
      void fireGameAlert({ text: "Decoded the locations?" }).then(() => {
        setJigsawOpen(false);
        setMapClickable(true);
        return fireGameAlert({
          icon: "success",
          image: bulbImg,
          title: "Well done!",
          text:
            "Now that you have solved the puzzle, take a close look! This might give you a clue to move further.",
        });
      });
    }, 1300);
  }, [completeStep]);

  // --- Step 3: map code ---
  const handleMapSuccess = useCallback(() => {
    setMapSolved(true);
    setActiveModal(null);
    void fireGameAlert({ image: bulbImg, text: "Listening is your key to the way out." });
  }, []);

  // --- Step 4: word search for the colours (reference #8 -> #9 -> #10) ---
  const handleWordComplete = useCallback(() => setWordFound(true), []);

  const handleWordNext = useCallback(async () => {
    if (wordNextBusyRef.current) return;
    wordNextBusyRef.current = true;
    const saved = await completeStep("word");
    wordNextBusyRef.current = false;
    if (!saved) return;
    setWordOpen(false);
    void fireGameAlert({
      image: bulbImg,
      title: "Brilliant!",
      text:
        "Guarded by barrels and maps of the land, the treasure waits in a chest where secrets stand—unlock it to reveal it all.",
      confirmButtonText: "Next",
    }).then(() => setWordSolved(true));
  }, [completeStep]);

  // --- Step 5: chest -> bearded pirate's name -> treasure (reference #12) ---
  const handleLastSuccess = useCallback(() => {
    freezeGame();
    setChestOpen(true);
  }, [freezeGame]);

  const [finishing, setFinishing] = useState(false);
  const handleFinish = useCallback(async () => {
    if (finishing) return;
    setFinishing(true);
    await saveFinalResultWithRetry();
    navigate("/thankyou");
  }, [finishing, saveFinalResultWithRetry, navigate]);

  // Room hotspots (old/room.php's jQuery click handlers) and whether each is live now.
  const hotspots = {
    books: { active: !finished, onClick: () => openOnly("books") },
    cupboard: { active: !finished && !drawerOpen, onClick: () => openOnly("drawer") },
    map: { active: !finished && mapClickable && !mapSolved, onClick: () => openOnly("map") },
    speaker: {
      active: !finished && mapSolved,
      onClick: () => {
        openOnly("audio");
        setFlagClickable(true);
      },
    },
    flag: {
      active: !finished && flagClickable && !wordSolved,
      onClick: () => openOnly("word"),
    },
    treasure: { active: !finished && wordSolved && !chestOpen, onClick: () => openOnly("last") },
  };
  const clickSpot = (name) => () => hotspots[name].active && hotspots[name].onClick();

  // old svg_bulb.php: replay the jigsaw, audio and word search once unlocked.
  const bulbs = [
    { key: "jigsaw", show: drawerOpen, label: "Jigsaw puzzle", onClick: () => openOnly("jigsaw") },
    { key: "audio", show: flagClickable, label: "Audio clue", onClick: () => openOnly("audio") },
    { key: "word", show: wordSolved, label: "Word puzzle", onClick: () => openOnly("word") },
  ];

  if (!dbReady) {
    return <div className="escape-loading">Please allow 60-90 seconds for the page to load.</div>;
  }

  const hud = (
    <div className="escape-hud">
      {!finished &&
        bulbs.map(
          (b) =>
            b.show && (
              <button
                key={b.key}
                type="button"
                className="escape-bulb"
                title={b.label}
                aria-label={b.label}
                onClick={b.onClick}
              >
                <img src={bulbImg} alt="" />
              </button>
            )
        )}
      <div className="wood-plank escape-timer">Time: {formatSecondsToClock(remainingSec)}</div>
    </div>
  );

  return (
    <div className={`escape-room-page${isMobile ? " is-mobile" : ""}`}>
      <div className="escape-room-stage">
        <svg
          className="escape-room-svg"
          xmlns="http://www.w3.org/2000/svg"
          viewBox={`0 0 ${scene.width} ${scene.height}`}
          preserveAspectRatio="xMidYMid slice"
        >
          <image href={scene.bg} width={scene.width} height={scene.height} />
          {/* SVG stacks by draw order: draw the speaker last so the treasure
              layer can never sit on top of it and swallow its clicks. */}
          {Object.entries(scene.layers)
            .sort(([a], [b]) => (a === "speaker") - (b === "speaker"))
            .map(([name, l]) => (
            <image
              key={name}
              id={name}
              href={l.href}
              x={l.x}
              y={l.y}
              width={l.w}
              height={l.h}
              className={hotspots[name].active ? "escape-hot" : undefined}
              onClick={clickSpot(name)}
            />
          ))}
          {Object.entries(scene.zones).map(([name, z]) => (
            <rect
              key={name}
              x={z.x}
              y={z.y}
              width={z.w}
              height={z.h}
              fill="transparent"
              className={hotspots[name].active ? "escape-hot" : undefined}
              onClick={clickSpot(name)}
            />
          ))}
        </svg>
        {hud}
      </div>

      {/* Full-screen puzzle scenes stay mounted once unlocked, so closing and
          reopening via a bulb keeps progress. */}
      {drawerOpen && (
        <div
          className="escape-scene"
          hidden={!jigsawOpen}
          style={{ backgroundImage: `url(${isMobile ? mobWoodBg : jigsawBg})` }}
        >
          {hud}
          <button
            type="button"
            className="escape-scene__x"
            aria-label="Close"
            onClick={() => setJigsawOpen(false)}
          >
            &times;
          </button>
          <div className="wood-plank escape-scene__banner">
            Solve the jigsaw by moving/dragging the pieces until it turns orange.
            <br /> Decode the coordinates to identify the locations.
          </div>
          <div className="escape-jigsaw-wrap">
            <JigsawPuzzle
              image={jisImg}
              size={3}
              aspectRatio={JIS_IMAGE_ASPECT_RATIO}
              startSolved={mapClickable}
              onSolved={handleJigsawSolved}
            />
          </div>
        </div>
      )}

      {flagClickable && (
        <div
          className="escape-scene"
          hidden={!wordOpen}
          style={{ backgroundImage: `url(${isMobile ? mobWoodBg : wordBg})` }}
        >
          {hud}
          <button
            type="button"
            className="escape-scene__x"
            aria-label="Close"
            onClick={() => setWordOpen(false)}
          >
            &times;
          </button>
          <div className="wood-plank escape-scene__banner">
            Solve the word puzzle to identify the hidden colors.
            <br /> Drag and select the words you find (Find {WORDS.length} Colors)
          </div>
          <Parchment className="escape-word-sheet">
            <WordSearch onComplete={handleWordComplete} />
            {wordFound && !wordSolved && (
              <div className="escape-word-done">
                <span className="escape-word-done__tick" aria-hidden="true">✓</span>
                <PirateButton onClick={handleWordNext}>Next</PirateButton>
              </div>
            )}
          </Parchment>
        </div>
      )}

      {chestOpen && (
        <div
          className="escape-scene escape-scene--final"
          style={{ backgroundImage: `url(${isMobile ? mobFinalBg : finalBg})` }}
        >
          <Parchment className="escape-final__card">
            <h2>Congratulations!</h2>
            <p>You have found the Queen&apos;s Jewels and completed the mission!</p>
            <PirateButton onClick={handleFinish} disabled={finishing}>
              {finishing ? "Saving…" : "Finish"}
            </PirateButton>
          </Parchment>
        </div>
      )}

      {activeModal === "books" && (
        <RoomModal title="Cryptic gibberish" onClose={closeModal} wide>
          <p className="escape-gibberish-intro">
            It’s complete Gibberish! Maybe that’s the new piracy language! Decode the cryptic
            gibberish and identify the code that might unlock something.
          </p>
          <ul className="escape-gibberish">
            {GIBBERISH_LINES.map((line) => (
              <li key={line}>
                <img src={jewelIconImg} alt="" /> {line}
              </li>
            ))}
          </ul>
          <PirateButton onClick={closeModal}>Close</PirateButton>
        </RoomModal>
      )}

      {activeModal === "drawer" && (
        <CodeModal
          prompt="Insert the 5 digit code to unlock"
          onSubmit={checkAnswer("drawer", handleDrawerSuccess)}
          onClose={closeModal}
        />
      )}

      {activeModal === "map" && (
        <CodeModal
          prompt="Insert the alphabetical code (3 characters)"
          onSubmit={checkAnswer("map", handleMapSuccess)}
          onClose={closeModal}
        />
      )}

      {activeModal === "audio" && (
        <RoomModal title="Listen carefully" onClose={closeModal}>
          {/* Unmounting on close stops playback (old hidden.bs.modal handler). */}
          <audio className="escape-audio" controls src={audioClip}>
            Your browser does not support the audio element.
          </audio>
          <div>
            <PirateButton onClick={closeModal}>Close</PirateButton>
          </div>
        </RoomModal>
      )}

      {activeModal === "last" && (
        <CodeModal
          prompt="Insert the name of the bearded pirate."
          onSubmit={checkAnswer("chest", handleLastSuccess)}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
